import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getLicense, getLicenseHistory, deleteLicense } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LicenseFormDialog from '../components/LicenseFormDialog';
import LicenseDocuments from '../components/LicenseDocuments';
import { 
  ArrowLeft, 
  Pencil, 
  Trash2, 
  FileText, 
  Calendar, 
  Building2, 
  User,
  Clock,
  History,
  AlertCircle,
  Download,
  File,
  Factory,
  Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const LicenseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isGestor } = useAuth();

  const [license, setLicense] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [licenseRes, historyRes] = await Promise.all([
        getLicense(id),
        getLicenseHistory(id)
      ]);
      setLicense(licenseRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      toast.error('Erro ao carregar licença');
      navigate('/licenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const handleDelete = async () => {
    try {
      await deleteLicense(id);
      toast.success('Licença excluída com sucesso');
      navigate('/licenses');
    } catch (error) {
      toast.error('Erro ao excluir licença');
    }
    setDeleteDialogOpen(false);
  };

  const getStatusBadge = (status) => {
    const config = {
      ativa: { label: 'Ativa', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      proxima_vencimento: { label: 'A Vencer', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      vencida: { label: 'Vencida', className: 'bg-rose-50 text-rose-700 border-rose-200' }
    };
    const { label, className } = config[status] || config.ativa;
    return <Badge variant="outline" className={`text-base px-3 py-1 ${className}`}>{label}</Badge>;
  };

  const getTypeName = (type) => {
    const types = {
      ae: 'AE',
      afe: 'AFE',
      alvara: 'Alvará',
      antt: 'ANTT',
      avcb: 'AVCB',
      certidao_crf: 'Certidão de Regularidade CRF',
      certidao_federal: 'Certidão Negativa de Débitos Federal',
      cetesb: 'CETESB',
      cnpj: 'CNPJ',
      marinha_mercante: 'Consulta Rep. Agenc. Desconsolidadora - Marinha Mercante',
      crq: 'CRQ',
      licenca_ambiental: 'Licença Ambiental',
      policia_civil: 'Polícia Civil',
      policia_federal: 'Polícia Federal',
      exercito: 'Exército',
      licenca_sanitaria: 'Licença Sanitária',
      vre: 'VRE',
      // Legacy types
      ambiental: 'Licença Ambiental',
      sanitaria: 'Licença Sanitária'
    };
    return types[type] || type;
  };

  const getCategoryInfo = (category) => {
    const config = {
      kn: { label: 'Kuehne+Nagel', icon: Factory, className: 'text-[#003366]' },
      fornecedor: { label: 'Fornecedor', icon: Users, className: 'text-gray-600' }
    };
    return config[category] || config.kn;
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const getActionLabel = (action) => {
    const actions = {
      create: 'Criação',
      update: 'Atualização',
      delete: 'Exclusão',
      upload: 'Upload de Arquivo'
    };
    return actions[action] || action;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Skeleton className="h-8 w-64 bg-gray-200" />
        <Card className="bg-white border-gray-200 shadow-lg">
          <CardContent className="p-6 space-y-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-6 bg-gray-100" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!license) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Licença não encontrada</h3>
        <Link to="/licenses">
          <Button className="mt-4 bg-[#003366] hover:bg-[#002244]">Voltar para lista</Button>
        </Link>
      </div>
    );
  }

  const categoryInfo = getCategoryInfo(license.category);
  const CategoryIcon = categoryInfo.icon;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="license-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/licenses')}
            className="text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{license.name}</h1>
            <p className="text-gray-500 mt-1">{getTypeName(license.type)}</p>
          </div>
        </div>
        {isGestor() && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(true)} className="bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:shadow-md transition-all" data-testid="edit-btn">
              <Pencil className="w-4 h-4 mr-2" />
              Editar
            </Button>
            <Button 
              variant="destructive"
              className="bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 hover:shadow-md transition-all"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          </div>
        )}
      </div>

      {/* License Info */}
      <Card className="bg-white border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
        <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100">
          <CardTitle className="text-lg text-gray-900 flex items-center gap-2 font-semibold">
            <FileText className="w-5 h-5 text-[#003366]" />
            Detalhes da Licença
          </CardTitle>
          {getStatusBadge(license.status)}
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Category Badge */}
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg w-fit border border-gray-100">
            <CategoryIcon className={`w-5 h-5 ${categoryInfo.className}`} />
            <span className="text-gray-700 font-semibold">{categoryInfo.label}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <p className="text-sm text-gray-500">CNPJ</p>
              <code className="text-lg font-mono text-gray-900 bg-white px-3 py-1 rounded border border-gray-200">
                {license.license_number}
              </code>
            </div>
            <div className="space-y-1 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <Building2 className="w-4 h-4" /> Órgão Emissor
              </p>
              <p className="text-lg text-gray-900 font-semibold">{license.issuing_authority}</p>
            </div>
            {license.protocol_number && (
              <div className="space-y-1 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-testid="detail-protocol">
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <FileText className="w-4 h-4" /> Número de Protocolo
                </p>
                <p className="text-lg text-gray-900 font-semibold font-mono">{license.protocol_number}</p>
              </div>
            )}
            {license.area && (
              <div className="space-y-1 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-testid="detail-area">
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Building2 className="w-4 h-4" /> Área
                </p>
                <p className="text-lg text-gray-900 font-semibold">
                  {{ fiscal: 'Fiscal', juridico: 'Jurídico', licenciamento: 'Licenciamento/Regulatório' }[license.area] || license.area}
                </p>
              </div>
            )}
            <div className="space-y-1 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <Calendar className="w-4 h-4" /> Data de Emissão
              </p>
              <p className="text-lg text-gray-900 font-semibold">{formatDate(license.issue_date)}</p>
            </div>
            <div className="space-y-1 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <Calendar className="w-4 h-4" /> Data de Vencimento
              </p>
              <p className="text-lg text-gray-900 font-semibold">
                {license.indeterminate_expiry || !license.expiry_date ? 'Indeterminado' : formatDate(license.expiry_date)}
              </p>
            </div>
            {license.responsible_person && (
              <div className="space-y-1 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <User className="w-4 h-4" /> Responsável
                </p>
                <p className="text-lg text-gray-900 font-semibold">{license.responsible_person}</p>
              </div>
            )}
          </div>

          {license.description && (
            <div className="pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500 mb-2 font-semibold">Descrição</p>
              <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{license.description}</p>
            </div>
          )}

          {license.notes && (
            <div className="pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500 mb-2 font-semibold">Observações</p>
              <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{license.notes}</p>
            </div>
          )}

          <div className="pt-4 border-t border-gray-100 flex items-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Criado em: {formatDateTime(license.created_at)}
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Atualizado em: {formatDateTime(license.updated_at)}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents (Arquivo / Protocolo) */}
      <LicenseDocuments license={license} canEdit={isGestor()} onChanged={fetchData} />

      {/* History */}
      <Card className="bg-white border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
        <CardHeader className="border-b border-gray-100">
          <CardTitle className="text-lg text-gray-900 flex items-center gap-2 font-semibold">
            <History className="w-5 h-5 text-[#003366]" />
            Histórico de Modificações
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {history.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma modificação registrada</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((log, index) => (
                <div 
                  key={log.id} 
                  className={`flex gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors ${index < history.length - 1 ? 'border-b border-gray-100' : ''}`}
                  data-testid={`history-item-${log.id}`}
                >
                  <div className={`w-3 h-3 mt-2 rounded-full ${
                    log.action === 'create' ? 'bg-emerald-500' : 
                    log.action === 'delete' ? 'bg-rose-500' : 
                    log.action === 'upload' ? 'bg-violet-500' : 'bg-blue-500'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900">{getActionLabel(log.action)}</span>
                      <span className="text-sm text-gray-500">por {log.user_name}</span>
                    </div>
                    <p className="text-xs text-gray-500">{formatDateTime(log.timestamp)}</p>
                    {log.action === 'update' && Object.keys(log.changes).length > 0 && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Campos alterados:</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.keys(log.changes).filter(k => k !== 'updated_at' && k !== 'status').map(key => (
                            <Badge key={key} variant="outline" className="text-gray-600 border-gray-300 bg-white">
                              {key}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {log.action === 'upload' && log.changes.file_name && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-100">
                        <p className="text-xs text-gray-600">Arquivo: {log.changes.file_name}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200 shadow-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900">Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Tem certeza que deseja excluir a licença &ldquo;{license.name}&rdquo;? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-gray-700 border-gray-300 hover:bg-gray-50">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white shadow-lg"
              data-testid="confirm-delete-btn"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit License Dialog */}
      <LicenseFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        licenseId={id}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default LicenseDetail;
